/* ==========================================================================
   Split Media With Text — section behaviour
   Handles: play/pause overlay, poster restoration on video end,
            auto-play on scroll into view.

   Loaded via <script defer> from sections/split-media-text.liquid.
   Works for all Shopify-hosted video instances on the page.
   YouTube / Vimeo iframes handle their own controls and ended state.
   ========================================================================== */

(function () {
  'use strict';

  // Guard against double-execution if the script tag is rendered more than once.
  if (window._vwtLoaded) return;
  window._vwtLoaded = true;

  /** @type {Map<string, {init: Function, teardown: Function}>} */
  const controllers = new Map();

  /* --------------------------------------------------------------------------
     Per-section controller factory
  -------------------------------------------------------------------------- */

  /**
   * @param {HTMLElement} sectionEl  The #shopify-section-{id} wrapper element.
   * @returns {{ init: Function, teardown: Function }}
   */
  function createController(sectionEl) {
    let viewObserver   = null;
    let loadWatcher    = null;
    let playController = null;

    /* ------------------------------------------------------------------
       Play / pause overlay + poster restoration on video end
       toggle is null when enable_video_controls is true (no overlay button rendered).
       Poster restoration still runs in that case.
    ------------------------------------------------------------------ */
    function bindPlayToggle() {
      const mediaEl = sectionEl.querySelector('[data-vwt-media]');
      if (!mediaEl) return;

      const toggle    = sectionEl.querySelector('[data-vwt-toggle]');
      const iconPlay  = toggle && toggle.querySelector('.vwt__play-toggle-icon--play');
      const iconPause = toggle && toggle.querySelector('.vwt__play-toggle-icon--pause');

      // Set icon state BEFORE revealing the button — no visible flash.
      function showPlayIcon() {
        iconPlay.hidden  = false;
        iconPause.hidden = true;
        toggle.setAttribute('aria-pressed', 'false');
        toggle.hidden = false;
      }

      function showPauseIcon() {
        iconPause.hidden = false;
        iconPlay.hidden  = true;
        toggle.setAttribute('aria-pressed', 'true');
        toggle.hidden = false;
      }

      // Remove the video from the DOM and strip [loaded] so the poster and
      // overlays are restored via CSS. Re-arms the watcher for the next play.
      function restorePoster(video) {
        if (toggle) toggle.hidden = true;
        video.remove();
        mediaEl.removeAttribute('loaded');
        armLoadWatcher();
      }

      function attachToVideo() {
        const video = mediaEl.querySelector('video');
        if (!video) return; // YouTube / Vimeo — no native <video> element

        // Abort any listeners from the previous play cycle before adding new ones.
        if (playController) playController.abort();
        playController = new AbortController();
        const { signal } = playController;

        if (toggle) {
          video.addEventListener('play',  showPauseIcon, { signal });
          video.addEventListener('pause', showPlayIcon,  { signal });

          toggle.addEventListener('click', function () {
            if (video.paused || video.ended) {
              video.play();
            } else {
              video.pause();
            }
          }, { signal });

          // Sync initial icon with actual playback state before making button visible.
          if (video.paused) {
            showPlayIcon();
          } else {
            showPauseIcon();
          }
        }

        // Restore the poster when the video ends. When looping is on the
        // ended event never fires, so this is naturally a no-op in that case.
        video.addEventListener('ended', function () {
          playController = null;
          restorePoster(video);
        }, { signal });
      }

      function armLoadWatcher() {
        if (loadWatcher) {
          loadWatcher.disconnect();
          loadWatcher = null;
        }
        loadWatcher = new MutationObserver(function () {
          if (!mediaEl.hasAttribute('loaded')) return;
          loadWatcher.disconnect();
          loadWatcher = null;
          attachToVideo();
        });
        loadWatcher.observe(mediaEl, { attributes: true, attributeFilter: ['loaded'] });
      }

      // Already loaded (e.g. theme editor section reload).
      if (mediaEl.hasAttribute('loaded')) {
        attachToVideo();
        return;
      }

      armLoadWatcher();
    }

    /* ------------------------------------------------------------------
       Auto-play on scroll into view (respects prefers-reduced-motion)
    ------------------------------------------------------------------ */
    function initAutoPlay() {
      const mediaEl = sectionEl.querySelector('[data-play-on-view]');
      if (!mediaEl) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      viewObserver = new IntersectionObserver(
        function (entries) {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const btn = mediaEl.querySelector('button');
            if (btn) btn.click();
            viewObserver.unobserve(mediaEl);
          }
        },
        { threshold: 0.1 }
      );

      viewObserver.observe(mediaEl);
    }

    /* ------------------------------------------------------------------
       Public interface
    ------------------------------------------------------------------ */
    function init() {
      bindPlayToggle();
      initAutoPlay();
    }

    function teardown() {
      if (viewObserver)   { viewObserver.disconnect();  viewObserver   = null; }
      if (loadWatcher)    { loadWatcher.disconnect();   loadWatcher    = null; }
      if (playController) { playController.abort();     playController = null; }
    }

    return { init, teardown };
  }

  /* --------------------------------------------------------------------------
     Lifecycle helpers
  -------------------------------------------------------------------------- */

  function initSection(sectionEl) {
    if (!sectionEl || !sectionEl.querySelector('[data-vwt-media]')) return;

    // Tear down any existing controller (e.g. theme editor re-load).
    const existing = controllers.get(sectionEl.id);
    if (existing) existing.teardown();

    const ctrl = createController(sectionEl);
    controllers.set(sectionEl.id, ctrl);
    ctrl.init();
  }

  function teardownSection(sectionId) {
    const key  = 'shopify-section-' + sectionId;
    const ctrl = controllers.get(key);
    if (!ctrl) return;
    ctrl.teardown();
    controllers.delete(key);
  }

  /* --------------------------------------------------------------------------
     Initialise all sections present at page load
  -------------------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    for (const mediaEl of document.querySelectorAll('[data-vwt-media]')) {
      const sectionEl = mediaEl.closest('[id^="shopify-section-"]');
      if (sectionEl) initSection(sectionEl);
    }
  });

  /* --------------------------------------------------------------------------
     Theme editor events
  -------------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (event) {
    const sectionEl = document.getElementById('shopify-section-' + event.detail.sectionId);
    initSection(sectionEl);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardownSection(event.detail.sectionId);
  });

})();
